/**
 * The Backblaze guide's cost calculator: a gigabyte slider that reads the
 * same shared price constant as the desktop wizard's own estimate and the
 * Providers pricing table, so all three quote the same number for the same
 * amount of data.
 */

import {
  BACKBLAZE_FREE_GB,
  backblazeCostPhrase,
  estimateBackblazeMonthlyCostForGb,
} from "@bigbear/shared";
import { useState } from "react";

/** Generous enough to cover a large personal vault without the slider
 * feeling like it's measuring a datacentre. */
const MAX_GB = 2000;

const STEP_GB = 5;

const DEFAULT_GB = 100;

/** Literally the same three-way split the wizard's teaser uses — the shared
 * phrase, sentence-cased, because here it stands on its own rather than
 * trailing off a clause. Not exported: nothing outside this component calls
 * it. */
function calculatorLine(gigabytes: number): string {
  const phrase = backblazeCostPhrase(estimateBackblazeMonthlyCostForGb(gigabytes));
  return `${phrase.charAt(0).toUpperCase()}${phrase.slice(1)}.`;
}

export function BackblazeCalculator() {
  const [gigabytes, setGigabytes] = useState(DEFAULT_GB);

  return (
    <section className="panel calculator" id="calculator">
      <h2>Estimate your own cost</h2>
      <p className="muted">
        Drag the slider to about how much data you'd back up. The first {BACKBLAZE_FREE_GB} GB
        stored is free every month, no matter what.
      </p>
      <div className="field">
        <label htmlFor="backblaze-calculator-gb">Vault size: {gigabytes} GB</label>
        <input
          id="backblaze-calculator-gb"
          type="range"
          min={0}
          max={MAX_GB}
          step={STEP_GB}
          value={gigabytes}
          onChange={(e) => setGigabytes(Number(e.target.value))}
        />
      </div>
      <p className="calculator__result" data-testid="backblaze-calculator-result">
        {calculatorLine(gigabytes)}
      </p>
    </section>
  );
}
